import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import AppDataContext from '../../contexts/appData.context';
import { sanitizeLoggedUrl } from '../../helpers/sanitizeLoggedUrl';
import { getAccessTokenClaims } from '../SecurityProvider/oktaAuthInstance';
import { AOCircularProgress } from '../AOCircularProgress';
import { Snackbar } from '../AORedesign';
import { MSAThemeProvider } from '../MSAThemeProvider/src';
import './App.css';
import AppAxiosInterceptor from './AppAxiosInterceptor';
import AppSnackbar from './AppSnackbar';
import RenderedApp from './RenderedApp';

datadogLogs.init({
  clientToken: import.meta.env.REACT_APP_DD_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: 'aoc-ui',
  env: import.meta.env.REACT_APP_ENV,
  version: import.meta.env.REACT_APP_COMMIT_SHORT_SHA,
  silentMultipleInit: true,
  trackAnonymousUser: true,
  sessionSampleRate: 100, // defaults to 100
  forwardConsoleLogs: ['log', 'error', 'info'], // not found in aoc-ui: 'warn','debug'
  forwardErrorsToLogs: true, // defaults to true
  // Not 'all'. 'all' includes csp-violation, which makes browser-logs attach its own
  // securitypolicyviolation listener and log a second, unsanitised copy at status:error with the
  // full blocked URI inside `message`, where the beforeSend scrubber below cannot reach it. That
  // copy has leaked an Okta authorization code. The listener further down logs strictly more.
  forwardReports: ['intervention', 'deprecation'],
  beforeSend: (log) => {
    if (log.message === 'null') {
      return false; // This will prevent the log from being sent
    }
    // CSP violation URLs can carry tokens, including percent-encoded inside a path.
    if (log.message === 'csp_violation') {
      for (const key of ['blockedURI', 'documentURI', 'sourceFile', 'blockedURL', 'documentURL']) {
        // Guarded so an absent field is not created as `undefined`.
        if (typeof log[key] === 'string') {
          log[key] = sanitizeLoggedUrl(log[key]);
        }
      }
    }
    return true;
  },
});

datadogRum.init({
  applicationId: import.meta.env.REACT_APP_DD_ID,
  clientToken: import.meta.env.REACT_APP_DD_CLIENT_TOKEN,
  service: 'aoc-ui',
  env: import.meta.env.REACT_APP_ENV,
  version: import.meta.env.REACT_APP_COMMIT_SHORT_SHA,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 20,
  defaultPrivacyLevel: 'mask-user-input',
});

// Forward CSP violations to Datadog so a resource the policy refuses is visible without waiting
// for someone to report a broken feature. Filter on `disposition`: `enforce` means a real user
// lost a real resource. `policy` is a prefix of the policy THIS document was served -- a tab keeps
// its header for its whole life, and some traffic sits behind a proxy that substitutes its own CSP,
// so "whose policy refused this" is not safe to assume. Policy text carries no secrets.
document.addEventListener('securitypolicyviolation', (event) => {
  datadogLogs.logger.warn('csp_violation', {
    directive: event.violatedDirective,
    effectiveDirective: event.effectiveDirective,
    blockedURI: event.blockedURI,
    sourceFile: event.sourceFile,
    lineNumber: event.lineNumber,
    columnNumber: event.columnNumber,
    sample: event.sample,
    disposition: event.disposition,
    documentURI: event.documentURI,
    policy: event.originalPolicy?.slice(0, 120),
  });
});

const App = () => {
  const [appData, setAppData] = useState();
  const [appOnModernBrowser, setAppOnModernBrowser] = useState(true);
  const [browserName, setBrowserName] = useState('');
  const [snackbarData, setSnackbarData] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [serviceUnavailable, setServiceUnavailable] = useState(false);
  const accessTokenClaims = getAccessTokenClaims();
  const userId = accessTokenClaims?.SamAccountName;
  const emailAddress = accessTokenClaims?.email;
  datadogRum.setUser({
    id: userId,
    email: emailAddress,
  });
  datadogRum.setGlobalContext({
    afe_session_id: localStorage.getItem('afe-session-id'),
  });
  const updateDDLogsGlobalContext = () => {
    const sessionId = localStorage.getItem('afe-session-id');
    const traceId = localStorage.getItem('afe-trace-id');

    if (sessionId && traceId) {
      datadogLogs.setGlobalContext({
        env: import.meta.env.REACT_APP_ENV,
        sessionId,
        traceId,
      });
    }
  };
  updateDDLogsGlobalContext();
  useEffect(() => {
    // Detect browser
    // https://codepedia.info/detect-browser-in-javascript#:~:text=To%20detect%20user%20browser%20information,to%20identify%20the%20user%20browser.&text=Now%20call%20this%20JS%20function,browser%20name%20on%20page%20load.

    const browserName = ((agent) => {
      switch (true) {
        case agent.indexOf('edge') > -1:
          return 'MS Edge';
        case agent.indexOf('edg/') > -1:
          return 'Edge (chromium based)';
        case agent.indexOf('opr') > -1 && !!window.opr:
          return 'Opera';
        case agent.indexOf('chrome') > -1 && !!window.chrome:
          return 'Chrome';
        case agent.indexOf('trident') > -1:
          return 'MS Internet Explorer';
        case agent.indexOf('firefox') > -1:
          return 'Mozilla Firefox';
        case agent.indexOf('safari') > -1:
          return 'Safari';
        default:
          return 'this browser';
      }
    })(window.navigator.userAgent.toLowerCase());

    setBrowserName(browserName);

    const unsupportedBrowsers = ['MS Internet Explorer'];

    if (unsupportedBrowsers.indexOf(browserName) > -1) {
      setAppOnModernBrowser(false);
    }

    console.info('Agents Only Cloud | browser:', browserName);
    // Print to console the git commit id as version
    console.info('Agents Only Cloud | version:', import.meta.env.REACT_APP_COMMIT_SHORT_SHA);
  }, []);

  const setSelectedAppetiteProduct = (selectedAppetiteProduct) => {
    setAppData({ ...appData, selectedAppetiteProduct });
  };

  const setAppetiteData = (appetiteData) => {
    setAppData({ ...appData, appetiteData });
  };

  const clearAppetiteData = () => {
    const appDataCopy = { ...appData };
    delete appDataCopy.appetiteData;
    setAppData({ ...appDataCopy });
  };

  const setPreviousPageName = (previousPageName) => {
    setAppData({ ...appData, previousPageName });
  };

  const setClaimsInquiry = (claimsInquiry) => {
    setAppData({ ...appData, claimsInquiry });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    handleSetSnackbarData(null);
  };

  const handleSetSnackbarData = (data) => {
    setSnackbarData(data);
  };

  const setShowServiceUnavailable = (show) => {
    setServiceUnavailable(show);
  };

  const updateDashboardCounts = (key, data) => {
    setAppData((prevState) => ({ ...prevState, dashboard: { ...prevState?.dashboard, [key]: data } }));
  };

  return appOnModernBrowser ? (
    <MSAThemeProvider>
      <AppAxiosInterceptor />

      <AppDataContext.Provider
        value={{
          ...appData,
          serviceUnavailable,
          setAppetiteData,
          clearAppetiteData,
          setPreviousPageName,
          setSelectedAppetiteProduct,
          setClaimsInquiry,
          handleSetSnackbarData,
          setLoadingMessage,
          setShowServiceUnavailable,
          updateDashboardCounts,
        }}>
        <Helmet>
          <title>Main Street America | Agents Only</title>
          <meta
            name="description"
            content="Main Street America Insurance offers property and casualty insurance products for individuals, families, and businesses. Learn more about our offerings here."
          />
        </Helmet>
        <RenderedApp />

        {/* Deprecate this Snackbar and leverage AppSnackbar below */}
        <Snackbar
          open={snackbarData?.message?.length}
          onClose={handleCloseSnackbar}
          message={!snackbarData?.severity?.length && snackbarData?.message}
          action={
            !snackbarData?.severity?.length && (
              <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )
          }>
          {snackbarData?.severity?.length && (
            <Alert onClose={handleCloseSnackbar} variant="filled" severity={snackbarData?.severity}>
              {snackbarData?.message}
            </Alert>
          )}
        </Snackbar>

        <AOCircularProgress visible={loadingMessage?.length?.length > 0}>{loadingMessage}</AOCircularProgress>
      </AppDataContext.Provider>

      <AppSnackbar />
    </MSAThemeProvider>
  ) : (
    <Alert severity="info">{`This application does not support ${browserName}, please use a modern browser.`}</Alert>
  );
};

export default App;
